// Shared TanStack Query hooks + key factory.
//
// Centralising the keys means pages that read the same endpoint (e.g. /profiles
// is read by the Dashboard, ProfileList, Sidebar and Settings) share ONE cached
// fetch instead of each doing their own. apiFetch stays the fetcher; Query only
// adds caching/staleness/background refetch on top.
//
// Keys are namespaced by impersonation id so an admin impersonating a user never
// sees their own cached data collide with the impersonated user's. Invalidate
// with the bare prefix (e.g. QK.profiles) — TanStack matches by prefix, so it
// catches every impersonation-namespaced variant.

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useImpersonation } from './context/ImpersonationContext';

export const QK = {
  profiles: ['profiles'],
  profile: ['profile'],
  usage: ['usage'],
  company: ['company'],
  tender: ['tender'],
  settings: ['settings'],
  apiKeys: ['api-keys'],
  integrationProviders: ['integrations', 'providers'],
  integrationConnections: ['integrations', 'connections'],
  authMe: ['auth-me'],
  matches: ['matches'],
  cpvSearch: ['cpv-search'],
  cpvLabels: ['cpv-labels'],
};

function useImpId() {
  const { impersonating } = useImpersonation();
  return impersonating?.id ?? null;
}

export function useProfiles(options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.profiles, impId],
    queryFn: () => apiFetch('/profiles'),
    ...options,
  });
}

export function useProfile(id, options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.profile, id, impId],
    queryFn: () => apiFetch(`/profiles/${id}`),
    enabled: !!id,
    ...options,
  });
}

export function useUsage(options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.usage, impId],
    queryFn: () => apiFetch('/usage'),
    ...options,
  });
}

export function useCompany(options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.company, impId],
    queryFn: () => apiFetch('/company'),
    ...options,
  });
}

// Full-table CPV search (9,453 official codes, backend /cpv/search). Not
// impersonation-namespaced — the table is global, so every user shares the
// cache. Long staleTime: the table never changes within a session.
export function useCpvSearch(q, options = {}) {
  const query = (q || '').trim();
  return useQuery({
    queryKey: [...QK.cpvSearch, query.toLowerCase()],
    queryFn: () => apiFetch(`/cpv/search?q=${encodeURIComponent(query)}`),
    enabled: query.length >= 3,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

// Labels for arbitrary full-table CPV codes (chips showing codes outside the
// curated frontend tree). Global cache, same reasoning as useCpvSearch.
export function useCpvLabels(codes, options = {}) {
  const list = [...new Set((codes || []).filter(Boolean))].sort();
  return useQuery({
    queryKey: [...QK.cpvLabels, list.join(',')],
    queryFn: () => apiFetch(`/cpv/labels?codes=${encodeURIComponent(list.join(','))}`),
    enabled: list.length > 0,
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

export function useTender(id, options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.tender, id, impId],
    queryFn: () => apiFetch(`/tenders/${id}`),
    enabled: !!id,
    ...options,
  });
}

export function useSettings(options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.settings, impId],
    queryFn: () => apiFetch('/settings'),
    ...options,
  });
}

export function useApiKeys(options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.apiKeys, impId],
    queryFn: () => apiFetch('/api-keys'),
    ...options,
  });
}

export function useIntegrationProviders(options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.integrationProviders, impId],
    queryFn: () => apiFetch('/integrations/providers'),
    ...options,
  });
}

export function useIntegrationConnections(options = {}) {
  const impId = useImpId();
  return useQuery({
    queryKey: [...QK.integrationConnections, impId],
    queryFn: () => apiFetch('/integrations/connections'),
    ...options,
  });
}

export function useAuthMe(options = {}) {
  return useQuery({
    queryKey: QK.authMe,
    queryFn: () => apiFetch('/auth/me'),
    ...options,
  });
}
